import { AmountValidation } from "utils/validators/AmountValidation";
import { PaybackTimeMonthsValidation } from "utils/validators/PaybackTimeMonthsValidation";
import { SetupFeeValidation } from "utils/validators/SetupFeeValidation";
import * as yup from "yup";

import type { Currency } from "@krea/shared/types/common";

import { BidTypeEnum, LeasingBidTypeEnum } from "@/features/common/enums";
import type { ILeasingBidInput } from "@/features/lender-responses/types/lender-response";

export const leasingBidSchema: (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<ILeasingBidInput> = (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) =>
  yup.object().shape({
    type: yup
      .string()
      .required("Type is required")
      .oneOf([BidTypeEnum.LEASING])
      .default(BidTypeEnum.LEASING),
    amount: AmountValidation(currency, formatWithCurrency),
    paybackTimeMonths: PaybackTimeMonthsValidation().max(
      120,
      (schema) => `Repayment time can't be greater than ${schema.max} months`,
    ),
    downPayment: yup
      .number()
      .required("First payment increased with (i.e. down payment) is required"),
    monthlyPayment: yup.number().required("Payment per 30 days is required"),
    terminalValuePercentage: yup
      .number()
      .required("Terminal value is required")
      .min(0, (schema) => `Terminal value can't be lower than ${schema.min}%`)
      .max(
        100,
        (schema) => `Terminal value can't be greater than ${schema.max}%`,
      ),
    setupFee: SetupFeeValidation(formatWithCurrency),
    insuranceMonthlyCost: yup.number().optional().nullable(),
    postalFee: yup.number().optional().nullable(),
    contractType: yup
      .string()
      .required("Type of contract is required")
      .oneOf(Object.values(LeasingBidTypeEnum)),
  });

export const leasingBidDefaultValues: ILeasingBidInput = {
  type: BidTypeEnum.LEASING,
  amount: undefined,
  downPayment: undefined,
  paybackTimeMonths: undefined,
  monthlyPayment: undefined,
  terminalValuePercentage: undefined,
  setupFee: undefined,
  postalFee: undefined,
  insuranceMonthlyCost: undefined,
  contractType: undefined,
};
